import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { FileSearch, Landmark, Plus, ScrollText, Search, Sparkles } from "lucide-react";
import { useInvalidateOn } from "@ncp/api-client";
import { formatDate, formatDateTime, useLocale, useTranslation, authorityText } from "@ncp/i18n";
import { Badge, Button, Card, EmptyState, Input, NativeSelect, PageHeader, SegmentBar, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, cn, SourceText } from "@ncp/ui";
import { useAdvocacyFramework, useAdvocacyOverview, useAdvocacyReviews, type Band, type ReviewRow } from "../features/advocacy/api";
import { BAND_COLOR, BAND_ORDER, BandBadge, ReviewStatusBadge, SectionTitle } from "../features/advocacy/components";
import { NewReviewDialog } from "../features/advocacy/NewReviewDialog";
import { AdvocacyMethodApproval } from "../features/advocacy/MethodApproval";

const tabs = ["reviews", "framework"] as const;
type Tab = (typeof tabs)[number];
const STATUSES = ["Received", "Screening", "Analysing", "InReview", "Adopted"] as const;
const STAGES = ["Draft", "InForce", "Proposed", "Consultation"] as const;

/**
 * Advocacy: laws, regulations and policies of other authorities recorded for a competition review. The section's method awaits the
 * Department's approval (condition 1.12, finding 7.26): until then instruments are recorded, nothing is screened or scored, no agent drafts
 * an assessment, and the proposed method is shown as awaiting approval.
 */
export function AdvocacyPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab");
  const tab: Tab = tabs.includes(requested as Tab) ? (requested as Tab) : "reviews";
  const [open, setOpen] = useState(params.get("new") === "1");
  const [filters, setFilters] = useState({ q: "", status: "", band: "", stage: "" });
  const overview = useAdvocacyOverview();
  const reviews = useAdvocacyReviews(filters);
  useInvalidateOn(["proposal.created", "proposal.updated", "agent.run"], [["advocacy"]]);
  const o = overview.data;
  const approved = o?.methodApproved ?? false;
  const rows = reviews.data ?? [];
  const hasAny = (o?.total ?? 0) > 0;

  return (
    <div className="min-w-0">
      <PageHeader title={t("backoffice:advocacy.title")} actions={<Button primary onClick={() => setOpen(true)}><Plus /> {t("backoffice:r3Library.advocacy.newInstrument")}</Button>} />
      {!approved && o ? <p className="mb-4 rounded-lg border border-sand-200 bg-sand-50 px-4 py-3 text-sm">{t("backoffice:r3Library.advocacy.suspended")}</p> : null}
      <dl className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {([
          ["recorded", o?.total],
          ["inReview", o?.inReview],
          ["adopted", o?.adopted],
          // Sent is what was sent: an adopted opinion whose sending to its issuing authority is recorded.
          ["sent", o?.sent],
          ...(approved ? [["highOrSevere", o?.highOrSevere ?? null]] as Array<[string, number | null | undefined]> : []),
        ] as Array<[string, number | null | undefined]>).map(([k, v]) => (
          <div key={k} className="min-w-0 rounded-lg border border-sand-200 bg-white px-4 py-3">
            <dt className="text-xs text-muted-foreground">{t(`backoffice:r3Library.advocacy.stats.${k}`)}</dt>
            <dd className="mt-1 font-heading text-2xl font-semibold tabular text-aeblack-950">{v ?? "—"}</dd>
          </div>
        ))}
      </dl>

      <Tabs value={tab} onValueChange={(v) => setParams((p) => { p.set("tab", v); return p; }, { replace: true })}>
        <TabsList variant="underline" className="mb-4">
          <TabsTrigger value="reviews" variant="underline"><FileSearch /> {t("backoffice:advocacy.tabs.reviews")}</TabsTrigger>
          <TabsTrigger value="framework" variant="underline"><ScrollText /> {t("backoffice:r3Library.advocacy.methodTab")}</TabsTrigger>
        </TabsList>
        <TabsContent value="reviews">
          <div className="grid gap-4 xl:grid-cols-4">
            <div className="min-w-0 space-y-3 xl:col-span-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-0 flex-1 basis-56">
                  <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-sand-500" aria-hidden />
                  <Input aria-label={t("backoffice:advocacy.filters.search")} value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder={t("backoffice:advocacy.filters.search")} className="px-9" dir={filters.q ? "auto" : undefined} />
                </div>
                <NativeSelect aria-label={t("backoffice:advocacy.filters.allStatuses")} value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className="w-44">
                  <option value="">{t("backoffice:advocacy.filters.allStatuses")}</option>
                  {STATUSES.map((s) => <option key={s} value={s}>{t(`backoffice:advocacy.status.${s}`)}</option>)}
                </NativeSelect>
                {approved ? (
                  <NativeSelect aria-label={t("backoffice:advocacy.filters.allBands")} value={filters.band} onChange={(e) => setFilters((f) => ({ ...f, band: e.target.value }))} className="w-40">
                    <option value="">{t("backoffice:advocacy.filters.allBands")}</option>
                    {BAND_ORDER.map((b) => <option key={b} value={b}>{t(`backoffice:advocacy.band.${b}`)}</option>)}
                  </NativeSelect>
                ) : null}
                <NativeSelect aria-label={t("backoffice:advocacy.filters.allStages")} value={filters.stage} onChange={(e) => setFilters((f) => ({ ...f, stage: e.target.value }))} className="w-44">
                  <option value="">{t("backoffice:advocacy.filters.allStages")}</option>
                  {STAGES.map((s) => <option key={s} value={s}>{t(`backoffice:advocacy.stage.${s}`)}</option>)}
                </NativeSelect>
              </div>
              {reviews.isLoading ? <Skeleton className="h-64" /> : rows.length === 0 ? (
                <EmptyState icon={Landmark} title={hasAny ? t("backoffice:advocacy.emptyFiltered") : t("backoffice:advocacy.empty")} />
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {rows.map((r) => <ReviewCard key={r.id} r={r} approved={approved} onOpen={() => navigate(`/advocacy/${r.id}`)} />)}
                </div>
              )}
            </div>
            <aside className="min-w-0 space-y-4">
              {approved ? (
                <Card className="p-5">
                  <SectionTitle>{t("backoffice:advocacy.byBand")}</SectionTitle>
                  {o && o.total > 0 ? (
                    <SegmentBar segments={BAND_ORDER.map((b) => ({ name: t(`backoffice:advocacy.band.${b}`), value: o.byBand.find((x) => x.band === b)?.count ?? 0 }))} palette={BAND_ORDER.map((b) => BAND_COLOR[b])} />
                  ) : <p className="text-sm text-muted-foreground">—</p>}
                </Card>
              ) : null}
              <Card className="p-5">
                <SectionTitle>{t("backoffice:advocacy.byAuthority")}</SectionTitle>
                {o && o.byAuthority.length > 0 ? (
                  <ul className="space-y-2 text-sm">
                    {o.byAuthority.map((a) => (
                      <li key={a.authority} className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 [overflow-wrap:anywhere]"><SourceText text={authorityText(a.authority, locale)} /></span>
                        <span className="text-xs tabular text-muted-foreground">{a.count}</span>
                        {approved && a.averageIndex != null ? <span className="w-10 text-end text-xs font-semibold tabular" style={{ color: BAND_COLOR[bandOf(a.averageIndex)] }}>{a.averageIndex}</span> : null}
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-sm text-muted-foreground">—</p>}
              </Card>
              {o && o.recent.length > 0 ? (
                <Card className="p-5">
                  <SectionTitle>{t("backoffice:advocacy.recent")}</SectionTitle>
                  <ul className="space-y-2 text-sm">
                    {o.recent.map((r) => (
                      <li key={r.id}>
                        <button type="button" onClick={() => navigate(`/advocacy/${r.id}`)} className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-start hover:bg-sand-50">
                          <span className="min-w-0 flex-1 truncate" dir="auto">{locale === "ar" && r.titleAr ? r.titleAr : r.title}</span>
                          <span className="text-[11px] text-muted-foreground">{formatDate(r.updatedAt, locale)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </Card>
              ) : null}
            </aside>
          </div>
        </TabsContent>
        <TabsContent value="framework"><FrameworkTab /></TabsContent>
      </Tabs>

      <NewReviewDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v && params.get("new")) setParams((p) => { p.delete("new"); return p; }, { replace: true }); }} onCreated={(id) => navigate(`/advocacy/${id}`)} />
    </div>
  );
}

function bandOf(score: number): Band { return score <= 0 ? "None" : score < 15 ? "Low" : score < 35 ? "Moderate" : score < 60 ? "High" : "Severe"; }

function ReviewCard({ r, approved, onOpen }: { r: ReviewRow; approved: boolean; onOpen: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const live = r.status === "Received" || r.status === "Screening" || r.status === "Analysing";
  return (
    <button type="button" onClick={onOpen} className={cn("group rounded-2xl border bg-white p-4 text-start transition hover:-translate-y-0.5 hover:shadow-md", r.status === "Adopted" ? "border-success-200 hover:border-success-300" : r.status === "InReview" ? "border-seablue-200 hover:border-seablue-300" : "border-sand-200 hover:border-aegold-300")}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {approved ? <span className="grid size-10 shrink-0 place-items-center rounded-lg font-heading text-sm font-bold text-white" style={{ background: BAND_COLOR[r.band ?? "None"] }}>{r.restrictivenessIndex}</span> : null}
          <div className="min-w-0">
            <p className="line-clamp-2 text-sm font-semibold text-aeblack-950" dir="auto">{locale === "ar" && r.titleAr ? r.titleAr : r.title}</p>
            <p className="truncate text-xs text-muted-foreground" dir="auto">{authorityText(r.issuingAuthority, locale)}</p>
          </div>
        </div>
        <ReviewStatusBadge status={r.status} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
        <Badge variant="outline" size="sm">{t(`backoffice:advocacy.instrumentTypes.${r.instrumentType}`, { defaultValue: r.instrumentType })}</Badge>
        <Badge variant="outline" size="sm">{t(`backoffice:advocacy.stage.${r.stage}`, { defaultValue: r.stage })}</Badge>
        {approved && r.band ? <BandBadge band={r.band} size="sm" /> : null}
        {approved && live ? <Badge variant="ai" size="sm" dot pulse><Sparkles /> {t(`backoffice:advocacy.status.${r.status}`)}</Badge> : null}
        <span className="ms-auto">{formatDateTime(r.updatedAt, locale)}</span>
      </div>
      {approved ? <p className="mt-2 text-xs text-muted-foreground">
        {t("backoffice:advocacy.provisionsFlagged", { flagged: r.flaggedProvisions, total: r.provisionCount })}{(r.highSeverity ?? 0) > 0 ? <span className="text-danger-700"> · {t("backoffice:advocacy.strongCount", { count: r.highSeverity })}</span> : null}{r.reference ? <span> · {authorityText(r.reference, locale)}</span> : null}
      </p> : null}
    </button>
  );
}

/** The method the tool applies – checklist catalogue with typical alternatives, the index and the guidance in the corpus. */
function FrameworkTab() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const fw = useAdvocacyFramework();
  const f = fw.data;
  if (fw.isLoading || !f) return <Skeleton className="h-96" />;
  const steps = [["one", "oneHint"], ["two", "twoHint"], ["three", "threeHint"]] as const;
  const groups = new Map<string, typeof f.checklist>();
  for (const it of f.checklist) groups.set(it.category, [...(groups.get(it.category) ?? []), it]);
  return (
    <div className="space-y-6">
      {!f.methodApproved ? <p className="rounded-lg border border-warning-200 bg-warning-50/60 px-4 py-3 text-sm text-warning-900">{t("backoffice:r3Library.advocacy.proposed")}</p> : null}
      <AdvocacyMethodApproval />
      <Card className="p-6">
        <SectionTitle>{t(f.methodApproved ? "backoffice:advocacy.framework.title" : "backoffice:r3Library.advocacy.proposedTitle")}</SectionTitle>
        <p className="text-sm leading-relaxed text-aeblack-900/80">{t("backoffice:advocacy.framework.intro")}</p>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {steps.map(([k, h]) => (
            <div key={k} className="rounded-xl border border-sand-200 bg-sand-50/60 p-4">
              <p className="font-heading text-sm font-semibold text-aegold-800">{t(`backoffice:advocacy.framework.steps.${k}`)}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t(`backoffice:advocacy.framework.steps.${h}`)}</p>
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-6">
        <SectionTitle>{t("backoffice:advocacy.framework.checklist")}</SectionTitle>
        <div className="overflow-x-auto" tabIndex={0}>
          <table className="w-full text-sm">
            <thead className="text-xs text-sand-600">
              <tr>
                <th className="px-2 py-2 text-start">{t("backoffice:advocacy.framework.checklistColumns.item")}</th>
                <th className="px-2 py-2 text-start">{t("backoffice:advocacy.framework.checklistColumns.question")}</th>
                <th className="hidden px-2 py-2 text-start lg:table-cell">{t("backoffice:advocacy.framework.checklistColumns.alternatives")}</th>
                <th className="px-2 py-2 text-end">{t("backoffice:advocacy.framework.checklistColumns.points")}</th>
                <th className="hidden px-2 py-2 text-start md:table-cell">{t("backoffice:advocacy.framework.checklistColumns.source")}</th>
              </tr>
            </thead>
            <tbody>
              {[...groups.entries()].map(([cat, items]) => (
                <GroupRows key={cat} label={locale === "ar" ? items[0]!.categoryAr : items[0]!.categoryEn} items={items} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-6">
          <SectionTitle>{t("backoffice:advocacy.framework.screen")}</SectionTitle>
          <p className="text-sm leading-relaxed text-aeblack-900/80">{locale === "ar" ? f.screeningMethodAr : f.screeningMethod}</p>
        </Card>
        <Card className="p-6">
          <SectionTitle>{t("backoffice:advocacy.framework.index")}</SectionTitle>
          <p className="text-sm leading-relaxed text-aeblack-900/80">{locale === "ar" ? f.indexMethodAr : f.indexMethod}</p>
          <div className="mt-4 flex h-3 w-full overflow-hidden rounded-full">
            {([["Low", 15], ["Moderate", 20], ["High", 25], ["Severe", 40]] as Array<[Band, number]>).map(([b, w]) => <span key={b} style={{ width: `${w}%`, background: BAND_COLOR[b] }} title={t(`backoffice:advocacy.band.${b}`)} />)}
          </div>
          <div className="mt-1 flex text-[11px] text-muted-foreground">
            {([["Low", 15], ["Moderate", 20], ["High", 25], ["Severe", 40]] as Array<[Band, number]>).map(([b, w]) => <span key={b} style={{ width: `${w}%` }}>{t(`backoffice:advocacy.band.${b}`)}</span>)}
          </div>
        </Card>
      </div>
      <Card className="p-6">
        <SectionTitle hint={t("backoffice:advocacy.framework.guidanceHint")}>{t("backoffice:advocacy.framework.guidance")}</SectionTitle>
        <ul className="grid gap-2 md:grid-cols-2">
          {f.guidance.map((g) => (
            <li key={g.id} className="rounded-xl border border-sand-200 p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-aeblack-950" dir="auto">{locale === "ar" && g.titleAr ? g.titleAr : <SourceText text={g.title} />}</p>
                <Badge variant="outline" size="sm">{t(`enums.corpusKind.${g.kind}`, { defaultValue: g.kind })}</Badge>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">{g.issuer ? authorityText(g.issuer, locale) : null}{g.issuedOn ? ` · ${formatDate(g.issuedOn, locale)}` : ""} · <span dir="ltr" lang="en" className="font-mono">{g.key}</span></p>
              {g.summary && (locale !== "ar" || /[\u0600-\u06FF]/.test(g.summary)) ? <p className="mt-1 line-clamp-3 text-xs leading-relaxed text-aeblack-900/75" dir="auto">{g.summary}</p> : null}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">{t("backoffice:advocacy.framework.instrumentTypes")}: {f.instrumentTypes.map((x) => t(`backoffice:advocacy.instrumentTypes.${x}`, { defaultValue: x })).join(" · ")}</p>
      </Card>
    </div>
  );
}

function GroupRows({ label, items }: { label: string; items: Array<{ code: string; questionEn: string; questionAr: string; alternativesEn: string[]; alternativesAr: string[]; basePoints: number; source: string }> }) {
  const { locale } = useLocale();
  const { t } = useTranslation();
  const sourceLabel = (source: string) => source.startsWith("OECD") ? t("backoffice:advocacy.framework.sources.oecd") : source.startsWith("UK CMA") ? t("backoffice:advocacy.framework.sources.cma") : <SourceText text={source} />;
  return (
    <>
      <tr className="bg-sand-50"><td colSpan={5} className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-aegold-800">{label}</td></tr>
      {items.map((it) => (
        <tr key={it.code} className="border-t border-sand-100 align-top">
          <td className="whitespace-nowrap px-2 py-2 font-semibold text-aeblack-950">{it.code}</td>
          <td className="px-2 py-2" dir="auto">{locale === "ar" ? it.questionAr : it.questionEn}</td>
          <td className="hidden px-2 py-2 text-xs text-muted-foreground lg:table-cell" dir="auto">{(locale === "ar" ? it.alternativesAr : it.alternativesEn).join(" · ")}</td>
          <td className="px-2 py-2 text-end tabular">{it.basePoints}</td>
          <td className="hidden min-w-56 px-2 py-2 text-xs leading-relaxed text-muted-foreground md:table-cell">{sourceLabel(it.source)}</td>
        </tr>
      ))}
    </>
  );
}
